// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  em,
  Flex,
  Group,
  Pagination,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { SearchRequest } from '@medplum/core';
import { getReferenceString, parseSearchRequest } from '@medplum/core';
import type { Communication } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { ThreadBucket } from '@polarissleep/messaging';
import {
  ChatList,
  markThreadRead,
  showErrorNotification,
  TenantScopedThreadChat,
  useThreadInbox,
} from '@polarissleep/messaging';
import { IconArrowLeft, IconMessageCircle } from '@tabler/icons-react';
import cx from 'clsx';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router';
import { getConfig } from '../../config';
import classes from './ThreadInbox.module.css';

interface ThreadInboxProps {
  query: string;
  threadId: string | undefined;
  bucket: ThreadBucket;
  getThreadUri: (topic: Communication) => string;
  onChange: (search: SearchRequest) => void;
  unreadUri: string;
  sentUri: string;
  allUri: string;
  recipientFilter?: string;
}

export function ThreadInbox(props: ThreadInboxProps): JSX.Element {
  const { query, threadId, bucket, getThreadUri, onChange, unreadUri, sentUri, allUri, recipientFilter } = props;

  const medplum = useMedplum();
  const currentSearch = useMemo(() => parseSearchRequest(`Communication?${query}`), [query]);

  const searchParams = useMemo(() => new URLSearchParams(query), [query]);
  const itemsPerPage = Number.parseInt(searchParams.get('_count') || '20', 10);
  const currentOffset = Number.parseInt(searchParams.get('_offset') || '0', 10);
  const currentPage = Math.floor(currentOffset / itemsPerPage) + 1;

  // sm < 768px: single panel. Responsive w prop handles md/lg sidebar width.
  // initialValue=false (assume desktop) + getInitialValueInEffect=false avoids layout flash on mount.
  const isMobile = useMediaQuery(`(max-width: ${em(768)})`, false, { getInitialValueInEffect: false });

  const { loading, error, threadMessages, selectedThread, total, currentUserRef, refreshThreadMessages } =
    useThreadInbox({
      query,
      threadId,
      recipientFilter,
      bucket,
    });

  // Mobile back link returns to the current bucket's list view.
  const bucketToUri: Record<ThreadBucket, string> = { unread: unreadUri, sent: sentUri, all: allUri };
  const backUri = bucketToUri[bucket];
  const showList = !isMobile || !selectedThread;
  const showChat = !isMobile || !!selectedThread;

  useEffect(() => {
    if (error) {
      showErrorNotification(error);
    }
  }, [error]);

  useEffect(() => {
    if (!selectedThread?.id || !currentUserRef) {
      return;
    }
    const id = selectedThread.id;
    markThreadRead(medplum, id, currentUserRef)
      .then((markedCount) => {
        if (markedCount > 0) {
          refreshThreadMessages().catch((err: Error) => showErrorNotification(err));
        }
      })
      .catch((err: Error) => showErrorNotification(err));
  }, [selectedThread?.id, currentUserRef, medplum, refreshThreadMessages]);

  const skeletonTitleWidths = [80, 72, 68, 64];
  const skeletonSubtitleWidths = [85, 78, 70, 60];

  return (
    <div className={classes.container}>
      <Flex direction="row" h="100%" w="100%">
        {showList && (
          <Flex
            direction="column"
            w={{ base: '100%', sm: '38%', md: '32%' }}
            maw={420}
            h="100%"
            className={isMobile ? undefined : classes.rightBorder}
          >
            <Paper h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
              <ScrollArea style={{ flex: 1 }} scrollbarSize={10} type="hover" scrollHideDelay={250}>
                <Flex h={64} align="center" justify="space-between" p="md">
                  <Group gap="xs">
                    <Button
                      component={Link}
                      to={unreadUri}
                      className={cx(classes.button, { [classes.selected]: bucket === 'unread' })}
                      size="compact-xs"
                      radius="xl"
                    >
                      New Messages
                    </Button>
                    <Button
                      component={Link}
                      to={sentUri}
                      className={cx(classes.button, { [classes.selected]: bucket === 'sent' })}
                      size="compact-xs"
                      radius="xl"
                    >
                      Sent
                    </Button>
                    <Button
                      component={Link}
                      to={allUri}
                      className={cx(classes.button, { [classes.selected]: bucket === 'all' })}
                      size="compact-xs"
                      radius="xl"
                    >
                      All Messages
                    </Button>
                  </Group>
                </Flex>
                <Divider />
                {loading ? (
                  <Stack gap="md" p="md">
                    {Array.from({ length: 10 }).map((_, index) => {
                      const titleWidth = skeletonTitleWidths[index % skeletonTitleWidths.length];
                      const subtitleWidth = skeletonSubtitleWidths[index % skeletonSubtitleWidths.length];
                      return (
                        <Flex key={index} gap="sm" align="flex-start">
                          <Skeleton height={40} width={40} radius="50%" />
                          <Box style={{ flex: 1 }}>
                            <Flex direction="column" gap="xs">
                              <Skeleton height={16} width={`${titleWidth}%`} />
                              <Skeleton height={14} width={`${subtitleWidth}%`} />
                            </Flex>
                          </Box>
                        </Flex>
                      );
                    })}
                  </Stack>
                ) : (
                  threadMessages.length > 0 && (
                    <ChatList
                      threads={threadMessages}
                      selectedCommunication={selectedThread}
                      getThreadUri={getThreadUri}
                      currentUserRef={currentUserRef}
                    />
                  )
                )}
                {threadMessages.length === 0 && !loading && <EmptyMessagesState />}
              </ScrollArea>
              {!loading && total !== undefined && total > itemsPerPage && (
                <Box p="md">
                  <Center>
                    <Pagination
                      value={currentPage}
                      total={Math.ceil(total / itemsPerPage)}
                      onChange={(page) => {
                        const offset = (page - 1) * itemsPerPage;
                        onChange({
                          ...currentSearch,
                          offset,
                        });
                      }}
                      size="sm"
                      siblings={1}
                      boundaries={1}
                    />
                  </Center>
                </Box>
              )}
            </Paper>
          </Flex>
        )}

        {showChat && selectedThread && (
          <Flex direction="column" style={{ flex: 1 }} h="100%">
            <Paper h="100%">
              <Stack h="100%" gap={0}>
                <Flex h={64} align="center" gap="xs" p="md">
                  <ActionIcon
                    component={Link}
                    to={backUri}
                    hiddenFrom="sm"
                    variant="subtle"
                    color="gray"
                    size="lg"
                    aria-label="Back to messages"
                  >
                    <IconArrowLeft size={20} />
                  </ActionIcon>
                  <Text fw={800} truncate fz="lg">
                    {selectedThread.topic?.text ?? 'Messages'}
                  </Text>
                </Flex>
                <Divider />
                <Flex direction="column" style={{ flex: 1 }} h="100%">
                  <TenantScopedThreadChat
                    key={`${getReferenceString(selectedThread)}`}
                    title={'Messages'}
                    thread={selectedThread}
                    excludeHeader={true}
                    organizationId={getConfig().organizationId}
                  />
                </Flex>
              </Stack>
            </Paper>
          </Flex>
        )}
        {showChat && !selectedThread && (
          <Flex direction="column" style={{ flex: 1 }} h="100%">
            <NoMessages />
          </Flex>
        )}
      </Flex>
    </div>
  );
}

function NoMessages(): JSX.Element {
  return (
    <Center h="100%" w="100%">
      <Stack align="center" gap="md">
        <ThemeIcon size={64} variant="light" color="gray">
          <IconMessageCircle size={32} />
        </ThemeIcon>
        <Stack align="center" gap="xs">
          <Text size="sm" c="dimmed" ta="center">
            Select a message from the list to view details
          </Text>
        </Stack>
      </Stack>
    </Center>
  );
}

function EmptyMessagesState(): JSX.Element {
  return (
    <Flex direction="column" h="100%" justify="center" align="center">
      <Stack align="center" gap="md" pt="xl">
        <IconMessageCircle size={64} color="var(--mantine-color-gray-4)" />
        <Text size="lg" c="dimmed" fw={500}>
          No messages found
        </Text>
      </Stack>
    </Flex>
  );
}
